/**
 * Row Action Options
 *
 * Configuration for individual row action menus.
 * Combines severity and status change options for the row-level context menu.
 */

import { TableActions } from '@/components/table/types';
import { Risk } from '@/types';
import type { VulnerabilityStatusMode } from '@/utils/vulnerabilityStatus.util';

import { getSeverityChangeOptions } from './severity';
import { getStatusChangeOptions } from './status';

/**
 * Creates row action configuration for a vulnerability
 * @param risk - The risk/vulnerability for this row
 * @param handlers - Action handler callbacks
 * @param isPraetorianUser - Whether user is a Praetorian user (default: false)
 * @param mode - The current vulnerability status mode (default: 'legacy')
 * @returns TableActions configuration with menu items
 */
export function createRowActions(
  risk: Risk | null,
  handlers: {
    onSeverityChange: (severity: string) => void;
    onStatusChange: (status: string) => void;
    onDelete: () => void;
  },
  isPraetorianUser = false,
  mode: VulnerabilityStatusMode = 'legacy'
): TableActions {
  if (!risk) {
    return {
      menu: {
        items: [],
      },
    };
  }

  const severityOptions = getSeverityChangeOptions();
  const statusOptions = getStatusChangeOptions(isPraetorianUser, mode);

  return {
    menu: {
      items: [
        // Severity change section
        ...severityOptions.map(option => ({
          label: option.label,
          type: option.type,
          icon: option.icon,
          onClick:
            option.type !== 'label' && option.value
              ? () => handlers.onSeverityChange(option.value!)
              : undefined,
        })),
        // Divider between sections
        { type: 'divider', label: 'Divider' },
        // Status change section
        ...statusOptions.map(option => ({
          label: option.label,
          type: option.type,
          icon: option.icon,
          onClick:
            option.type !== 'label'
              ? option.isDelete
                ? handlers.onDelete
                : option.value
                  ? () => handlers.onStatusChange(option.value!)
                  : undefined
              : undefined,
        })),
      ],
    },
  };
}
